import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import "../styles/EditProduct.css";

const IMGBB_KEY = process.env.REACT_APP_IMGBB_KEY;

export default function EditProduct() {
  const { shopId, productId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [image, setImage] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");

  const [price, setPrice] = useState("");
  const [offerPrice, setOfferPrice] = useState("");

  const [stock, setStock] = useState("");

  const [active, setActive] = useState(true);

  useEffect(() => {
    loadProduct();
  }, [productId]);

  async function loadProduct() {
    try {
      setLoading(true);

      const productRef = doc(db, "shopproducts", productId);
      const productSnap = await getDoc(productRef);

      if (!productSnap.exists()) {
        alert("Product not found.");
        navigate(`/shop/manage/${shopId}`);
        return;
      }

      const product = productSnap.data();

      setImage(product.image || "");
      setName(product.name || "");
      setDescription(product.description || "");
      setCategory(product.category || "");
      setBrand(product.brand || "");

      setPrice(product.price || "");
      setOfferPrice(product.offerPrice || "");

      setStock(product.stock || "");

      setActive(product.active ?? true);

    } catch (err) {
      console.error(err);
      alert("Failed to load product.");
    }

    setLoading(false);
  }

  if (loading) {
    return (
      <div className="add-product-page">
        Loading...
      </div>
    );
  }

    // ==========================
  // Select Image
  // ==========================

  function handleImage(e) {
    if (!e.target.files[0]) return;

    setImageFile(e.target.files[0]);

    setImage(URL.createObjectURL(e.target.files[0]));
  }

  // ==========================
  // Upload Image (if changed)
  // ==========================

  async function uploadImage() {
    if (!imageFile) return image;

    const formData = new FormData();
    formData.append("image", imageFile);

    const res = await fetch(
      `https://api.imgbb.com/1/upload?key=${IMGBB_KEY}`,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await res.json();

    if (!data.success) {
      throw new Error("Image upload failed");
    }

    return data.data.url;
  }

  // ==========================
  // Save Product
  // ==========================

  async function handleSave(e) {
    e.preventDefault();

    if (!name.trim()) {
      alert("Enter product name");
      return;
    }

    if (!price) {
      alert("Enter price");
      return;
    }

    try {
      setSaving(true);

      const imageUrl = await uploadImage();

      await updateDoc(
        doc(db, "shopproducts", productId),
        {
          image: imageUrl,

          name: name.trim(),

          description: description.trim(),

          category,

          brand,

          price: Number(price),

          offerPrice: Number(offerPrice || 0),

          stock: Number(stock || 0),

          active,

          updatedAt: serverTimestamp(),
        }
      );

      alert("Product updated successfully.");

      navigate(`/shop/manage/${shopId}`);

    } catch (err) {
      console.error(err);
      alert(err.message);

    } finally {
      setSaving(false);
    }
  }

  // ==========================
  // Delete Product
  // ==========================

  async function handleDelete() {

    const ok = window.confirm(
      "Delete this product permanently?"
    );

    if (!ok) return;

    try {

      setSaving(true);

      await deleteDoc(
        doc(db, "shopproducts", productId)
      );

      alert("Product deleted.");

      navigate(`/shop/manage/${shopId}`);

    } catch (err) {

      console.error(err);

      alert("Delete failed.");

    } finally {

      setSaving(false);

    }

  }

    return (
    <div className="add-product-page">

      <div className="add-product-card">

        <h2>Edit Product</h2>

        <form onSubmit={handleSave}>

          {/* Product Image */}

          <label className="image-upload">

            {image ? (
              <img
                src={image}
                alt="Product"
                className="preview-image"
              />
            ) : (
              <div className="image-placeholder">
                Select Product Image
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              hidden
              onChange={handleImage}
            />

          </label>

          {/* Product Name */}

          <input
            type="text"
            placeholder="Product Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          {/* Description */}

          <textarea
            rows="4"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Category */}

          <input
            type="text"
            placeholder="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />

          {/* Brand */}

          <input
            type="text"
            placeholder="Brand"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          />

          {/* Price */}

          <input
            type="number"
            placeholder="MRP"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            required
          />

          {/* Offer Price */}

          <input
            type="number"
            placeholder="Offer Price"
            value={offerPrice}
            onChange={(e) => setOfferPrice(e.target.value)}
          />

          {/* Stock */}

          <input
            type="number"
            placeholder="Available Stock"
            value={stock}
            onChange={(e) => setStock(e.target.value)}
          />

          {/* Active */}

          <div className="checkbox-row">

            <input
              id="active"
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />

            <label htmlFor="active">
              Product is Active
            </label>

          </div>

          {/* Buttons */}

          <div className="button-group">

            <button
              className="save-btn"
              type="submit"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>

            <button
              type="button"
              className="delete-btn"
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Product
            </button>

            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate("/shop/manage")}
            >
              Cancel
            </button>

          </div>

        </form>

      </div>

    </div>
  );
}
